import { BadgeCheckIcon } from "@heroicons/react/solid";
import { isEmpty, orderBy, partition } from "lodash";
import moment from "moment";
import { stringifyUrl } from "query-string";
import React, { useCallback, useMemo } from "react";
import { Link, RouteComponentProps, useLocation } from "react-router-dom";
import DisplayLabels from "../../../../components/labels/DisplayLabels";
import StatusDot from "../../../../components/StatusDot";
import { useHotspots } from "../../../../context/hotspots";
import { useTransmitScales } from "../../../../context/transmitScales";
import { avatarsApi } from "../../../../utils/constants";
import {
  ClassificationValue,
  formatHotspotName
} from "../../../../utils/hotspotUtils";
import { h3ToParentMemoized } from "../../../../utils/memoizedMethods";
import { PhpHotspot } from "../../../../utils/types";
import { useHntMarket } from "../../../../utils/useHntMarket";
import useTransmitScaleConfig from "../../../../utils/useTransmitScaleConfig";
import TablePagination from "../../../Workspace/common/TablePagination";
import usePagination, {
  PaginationKey
} from "../../../Workspace/common/usePagination";

const HotspotsInHexTab: React.FC<RouteComponentProps<{ h3Index: string }>> = (
  props
) => {
  const {
    hotspotsAroundCenter,
    hotspotRewardsSummary,
    walletVerificationStatusMap
  } = useHotspots();
  const {
    getHexTransmitScaleInfo,
    calculatedTransmitScales,
    isInteractiveHotspot,
    isTransmitScaleActive
  } = useTransmitScales();
  const { displayInCurrency } = useHntMarket();
  const forcedInteractiveIds = useTransmitScaleConfig(
    (state) => state.forcedInteractiveIds
  );
  const toggleIsActive = useTransmitScaleConfig(
    (state) => state.toggleIsActive
  );
  const toggleForceInteractivity = useTransmitScaleConfig(
    (state) => state.toggleForceInteractivity
  );

  const { search: queryParamsString } = useLocation();
  const { resolution } = getHexTransmitScaleInfo(props.match.params.h3Index);

  const hotspotsInHex = useMemo(() => {
    const allHotspotsInHex = hotspotsAroundCenter
      .filter(
        ({ location }) =>
          h3ToParentMemoized(location, resolution) ===
          props.match.params.h3Index
      )
      .map((hotspot) => ({
        hotspot,
        dayWeekAverageReward:
          hotspotRewardsSummary[hotspot.id]?.dayWeekAverage || 0,
        transmitScale: calculatedTransmitScales[hotspot.id] || 0,
        mostRecentAssertionTime: hotspot.last_assertion,
        isActive: isTransmitScaleActive(hotspot.id),
        isForcedInteractive: forcedInteractiveIds.includes(hotspot.id),
        isInteractive: isInteractiveHotspot(hotspot.last_poc_challenge)
      }));

    const sortedHotspotsInHex = orderBy(
      allHotspotsInHex,
      "mostRecentAssertionTime",
      "desc"
    );

    const [activeHotspotsInHex, disabledHotspotsInHex] = partition(
      sortedHotspotsInHex,
      "isActive"
    );

    return disabledHotspotsInHex.concat(activeHotspotsInHex);
  }, [
    calculatedTransmitScales,
    isTransmitScaleActive,
    forcedInteractiveIds,
    hotspotRewardsSummary,
    hotspotsAroundCenter,
    isInteractiveHotspot,
    props.match.params.h3Index,
    resolution
  ]);

  const { startIndex, endIndex } = usePagination(
    useCallback(
      (state) =>
        state.getPaginationInfo(
          PaginationKey.hexHotspots,
          hotspotsInHex.length,
          queryParamsString
        ),
      [hotspotsInHex.length, queryParamsString]
    )
  );

  if (isEmpty(hotspotsInHex)) {
    return (
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-50">
        There are no hotspots in this hex so far.
      </p>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="shadow overflow-hidden border-b border-gray-200 dark:border-gray-500 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
              <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    {hotspotsInHex.length} hotspot
                    {hotspotsInHex.length > 1 ? "s" : ""}
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Added to hex
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Daily rewards
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Transmit scale
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Interactive
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Activated
                  </th>

                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                  >
                    Contact
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500 dark:bg-gray-800">
                {hotspotsInHex
                  .slice(startIndex, endIndex)
                  .map(
                    ({
                      hotspot,
                      dayWeekAverageReward,
                      transmitScale,
                      mostRecentAssertionTime,
                      isActive,
                      isForcedInteractive,
                      isInteractive
                    }) => (
                      <tr key={hotspot.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            className="relative flex items-center space-x-3 group"
                            to={`/hotspots/${hotspot.id}/reward-scaling`}
                          >
                            <div className="flex-shrink-0">
                              <span className="inline-block relative">
                                <img
                                  className="h-10 w-10 rounded-full"
                                  src={stringifyUrl({
                                    url: `${avatarsApi}/bottts/${hotspot.id}.svg`,
                                    query: { w: 40, h: 40 }
                                  })}
                                  alt="Hotspot avatar"
                                />
                                <StatusDot
                                  hotspot={hotspot}
                                  withoutTooltip
                                  className="absolute top-0 right-0 block"
                                />
                              </span>
                            </div>
                            <div className="flex-1 min-w-0 focus:outline-none">
                              <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline inline-flex items-center">
                                {((hotspot as PhpHotspot).verified_by ||
                                  !!walletVerificationStatusMap[
                                    hotspot.owner
                                  ]) && (
                                  <BadgeCheckIcon className="mr-1 h-5 w-5 text-green-400" />
                                )}
                                {formatHotspotName(hotspot)}
                              </p>
                              <DisplayLabels
                                linkedUuid={hotspot.id}
                                size="small"
                                contextWalletAddresses={[hotspot.owner]}
                              />
                            </div>
                          </Link>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {mostRecentAssertionTime
                            ? moment
                                .unix(mostRecentAssertionTime)
                                .utc()
                                .fromNow()
                            : "-"}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {displayInCurrency(dayWeekAverageReward, {
                            needsDivision: true
                          })}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <ClassificationValue value={transmitScale} />
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {isInteractive ? (
                            "Yes"
                          ) : (
                            <div className="flex items-center">
                              <button
                                type="button"
                                className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none ${
                                  isForcedInteractive
                                    ? "bg-hotspotty-400"
                                    : "bg-gray-200"
                                }`}
                                aria-pressed="false"
                                aria-labelledby="toggle-force-interactive"
                                onClick={() =>
                                  toggleForceInteractivity(hotspot.id)
                                }
                              >
                                <span
                                  aria-hidden="true"
                                  className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200 ${
                                    isForcedInteractive
                                      ? "translate-x-5"
                                      : "translate-x-0"
                                  }`}
                                />
                              </button>
                            </div>
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <button
                              type="button"
                              className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none ${
                                isActive
                                  ? "bg-hotspotty-400 dark:bg-gray-100 dark:hover:bg-gray-200"
                                  : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400"
                              }`}
                              aria-pressed="false"
                              aria-labelledby="toggle-interactive"
                              onClick={() => toggleIsActive(hotspot.id)}
                            >
                              <span
                                aria-hidden="true"
                                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200 ${
                                  isActive ? "translate-x-5" : "translate-x-0"
                                }`}
                              />
                            </button>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Link
                              to={`/hotspots/${hotspot.id}/owner`}
                              className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                            >
                              Message
                            </Link>
                          </div>
                        </td>
                      </tr>
                    )
                  )}
              </tbody>
            </table>
            <TablePagination
              paginationKey={PaginationKey.hexHotspots}
              itemsCount={hotspotsInHex.length}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HotspotsInHexTab;
